'use client'

/**
 * CrestLevel — the level + title + XP-bar block of the identity crest.
 *
 * ⭐ ONE currency, one read. Takes the raw XP (users.renown, threaded through the session) and derives
 * everything: the level number, the earned title (The Adept …), and progress to the next level — all
 * from src/lib/xp/curve.ts + title.ts, the same math awardXp() writes with, so the crest can never
 * disagree with the ledger. See the register entry `level-system-awards-nothing-0820` for the split
 * this replaces (the navbar used to read a capped `favor`-derived level).
 *
 * Renders nothing until there is XP, so a brand-new account shows a clean crest rather than an empty
 * "Level 0 · 0 / 120" bar. The moment any action awards XP, the block appears.
 */
import { xpProgress } from '@/lib/xp/curve'
import { levelTitle } from '@/lib/xp/title'

const fmt = (n: number) => Math.round(n).toLocaleString('en-US')

export default function CrestLevel({
  xp,
  compact = false,
  className = '',
}: {
  xp: number | string | null | undefined
  /** Tighter type + spacing for the dropdown; the profile page uses the roomy default. */
  compact?: boolean
  className?: string
}) {
  const prog = xpProgress(xp)
  if (prog.xp <= 0) return null
  const title = levelTitle(prog.xp)

  return (
    <div className={className}>
      <div className="flex items-baseline justify-between gap-2">
        <span className={`font-semibold tracking-wide ${title.color} ${compact ? 'text-xs' : 'text-sm'}`}>
          {title.name}
        </span>
        <span className={`tabular-nums text-zinc-400 ${compact ? 'text-[11px]' : 'text-xs'}`}>
          Level {prog.level}
        </span>
      </div>
      <div
        className={`mt-1.5 w-full overflow-hidden rounded-full border border-zinc-700/60 bg-zinc-900 ${compact ? 'h-1.5' : 'h-2'}`}
        role="progressbar"
        aria-valuenow={Math.round(prog.pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Level ${prog.level}, ${Math.round(prog.pct)}% to next level`}
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-amber-500 via-amber-400 to-amber-300 shadow-[0_0_8px_-1px_rgba(251,191,36,0.55)] transition-[width] duration-700 ease-out"
          style={{ width: `${prog.pct}%` }}
        />
      </div>
      <div className={`mt-1 tabular-nums text-zinc-500 ${compact ? 'text-[10px]' : 'text-[11px]'}`}>
        {fmt(prog.intoLevel)} / {fmt(prog.levelSpan)} XP &middot; {fmt(prog.toNext)} to Level {prog.level + 1}
      </div>
    </div>
  )
}
